import { type UserMetadata } from '@choco-admin/types';
import { Switch } from '@heroui/react';
import React, { useState, useCallback } from 'react';

import {
  EntitySearchModal,
  EntitySearchOption,
} from '../../../../../../../components/FormComponents';
import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { useChatUserOperations } from '../../../../../hooks';
import { useSearchSupplierUsers } from '../../../../../hooks/useSearchSupplierUsersForChat';
import { useChatPageContext } from '../../../contexts';

interface AddSupplierUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatId: string;
  supplierId: string;
}

export const AddSupplierUserModal: React.FC<AddSupplierUserModalProps> = ({
  isOpen,
  onClose,
  chatId,
  supplierId,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  // Default flow is a global distributor-user search; the operator can opt in
  // to narrow the list to just this chat's distributor's own users.
  const [scopeToSupplier, setScopeToSupplier] = useState(false);
  const { updateChatData } = useChatPageContext();
  const { addNotification } = useNotification();

  const { addUsers } = useChatUserOperations();

  const {
    users,
    isLoading: searchLoading,
    error: searchError,
    searchUsers,
  } = useSearchSupplierUsers({
    supplierId,
    scopeToSupplier,
  });

  const handleSearch = useCallback(
    (searchTerm: string) => {
      if (searchTerm.trim()) {
        searchUsers(searchTerm.trim());
      }
    },
    [searchUsers]
  );

  const handleConfirm = async (selectedEntity: EntitySearchOption) => {
    setError(null);
    setIsAdding(true);

    try {
      const updatedUsers = await addUsers([
        {
          chatId,
          userId: selectedEntity.id,
          userType: 'supplier',
          operation: 'add',
        },
      ]);

      // Write the server-true user list from the mutation response so the new
      // user appears in the list immediately.
      updateChatData((prevData) =>
        prevData ? { ...prevData, users: updatedUsers } : prevData
      );

      addNotification({
        title: 'User Added',
        message: 'User has been successfully added to the chat.',
        type: 'success',
      });
      onClose();
    } catch (confirmError) {
      addNotification({
        title: 'Add Failed',
        message:
          confirmError instanceof Error
            ? confirmError.message
            : 'Failed to add user to chat.',
        type: 'error',
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleErrorDismiss = () => {
    setError(null);
  };

  // A failed load must not read as "no users" — that is the silent-empty-list
  // bug the error banner replaces.
  let emptyMessage: string | undefined;
  if (users.length === 0 && !searchLoading && !searchError) {
    emptyMessage = scopeToSupplier
      ? 'This distributor has no users'
      : 'Type to search distributor users';
  }

  return (
    <EntitySearchModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Distributor User"
      searchPlaceholder="Search distributor users by name or email..."
      entityType="distributor user"
      isSearching={searchLoading}
      emptyMessage={emptyMessage}
      filterControl={
        <Switch
          size="sm"
          isSelected={scopeToSupplier}
          onValueChange={setScopeToSupplier}
          isDisabled={!supplierId}
          classNames={{ label: 'text-sm text-default-600' }}
        >
          Only show this distributor&apos;s users
        </Switch>
      }
      searchResults={users.map((user) => ({
        id: user.value,
        name: user.label,
        label: user.label,
        subtitle: (user.metaData as UserMetadata)?.email || '',
        metadata: {
          email: (user.metaData as UserMetadata)?.email || '',
          phone: (user.metaData as UserMetadata)?.phone || '',
          whatsApp:
            (user.metaData as UserMetadata)?.orderPreferences?.some(
              (pref: { type: string }) => pref.type === 'whatsapp'
            ) || false,
          sms:
            (user.metaData as UserMetadata)?.orderPreferences?.some(
              (pref: { type: string }) => pref.type === 'sms'
            ) || false,
          fax:
            (user.metaData as UserMetadata)?.orderPreferences?.some(
              (pref: { type: string }) => pref.type === 'fax'
            ) || false,
        },
      }))}
      onSearch={handleSearch}
      onConfirm={handleConfirm}
      isConfirming={isAdding}
      filterResultsLocally={scopeToSupplier}
      error={
        error ?? (searchError ? 'Could not load distributor users.' : null)
      }
      onErrorDismiss={handleErrorDismiss}
    />
  );
};
